import { LoadingButton } from "@mui/lab";
import { Box, Button, FormHelperText, Grid, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import {
  CheckoutState,
  SubscriptionContext,
  SubscriptionTab,
} from "contexts/Subscription";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddOnItemName } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { getParams, getSource } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import CardWrapper from "../components/CardWrapper";
import ManageEmailVolumeDialog from "./ManageEmailVolumeDialog";
import styles from "./styles";

const BENEFITS = [
  {
    icon: "📮",
    primary: "Get 150 additional automated emails each month",
    secondary: <>Expand your reach and connect with more brands</>,
  },
];

const AddOnsEmailVolume = () => {
  const source = getSource();
  const navigate = useNavigate();
  const {
    subscription,
    addOns,
    setTab,
    setOpenCheckoutDialog,
    upgradeDialogSource,
  } = useContext(SubscriptionContext);
  const { checkoutLoading } = useSubscription();
  const [openManageDialog, setOpenManageDialog] = useState(false);
  const canPurchase =
    !subscription?.emailVolumes ||
    subscription?.emailVolumes.length <
      addOns[AddOnItemName.email_volume_monthly]?.max_quantity;

  const hasPurchased =
    subscription?.emailVolumes && subscription?.emailVolumes.length > 0;

  const handleManage = () => {
    setOpenManageDialog(true);
  };

  const handleCheckout = async () => {
    setTab(SubscriptionTab.monthly);
    const state: CheckoutState = {
      plan: AddOnItemName.email_volume_monthly,
      source,
      pageSource: window.location.pathname,
      coupon: getParams("coupon"),
    };
    if (upgradeDialogSource) {
      setOpenCheckoutDialog(state);
    } else {
      navigate(`/${routes.subscriptions}`, {
        state,
      });
    }
  };

  return (
    <CardWrapper>
      <Grid item>
        <Typography variant="h5" component="div" gutterBottom>
          🔥 Increase your automated email volume
        </Typography>

        <Typography variant="h6" sx={{ color: "green.main" }} gutterBottom>
          ${addOns?.[AddOnItemName.email_volume_monthly]?.price.toFixed(2)}
          /mo
        </Typography>

        <Typography gutterBottom>
          Level up your outreach — send more brand pitches each month starting
          at just $25/mo.
        </Typography>

        {BENEFITS.map((benefit, index) => (
          <Grid container key={index} alignItems="center" sx={{ mb: 1 }}>
            <Grid
              container
              justifyContent="left"
              alignItems="center"
              item
              xs="auto"
            >
              <Box sx={styles.iconBlock}>{benefit.icon}</Box>
            </Grid>
            <Grid item xs md>
              <Typography>
                <strong>{benefit.primary}</strong>
              </Typography>
              <Typography>{benefit.secondary}</Typography>
            </Grid>
          </Grid>
        ))}
      </Grid>

      <Grid container item alignItems="center" gap={1}>
        {canPurchase && (
          <LoadingButton
            onClick={handleCheckout}
            disableElevation
            loading={checkoutLoading === AddOnItemName.email_volume_monthly}
            sx={{
              px: 2,
              color: "white",
              backgroundColor: "primary.main",
            }}
          >
            Purchase
          </LoadingButton>
        )}

        {hasPurchased && (
          <Button
            disableElevation
            onClick={handleManage}
            sx={{
              px: 2,
              color: "primary.main",
              backgroundColor: "#f4f8f6",
            }}
          >
            Manage{" "}
          </Button>
        )}
        <Grid item xs={12}>
          <FormHelperText sx={{ color: grey[600], mt: 1 }}>
            {hasPurchased ? (
              <em>
                You have purchased{" "}
                <strong>
                  {subscription?.emailVolumes?.length}{" "}
                  {subscription?.emailVolumes?.length === 1
                    ? "package"
                    : "packages"}
                </strong>
                . Click on Manage to view details.
              </em>
            ) : (
              <em>
                Limit {addOns[AddOnItemName.email_volume_monthly]?.max_quantity}{" "}
                per account. <strong>Save up to 33.3%</strong> by paying
                annually.
              </em>
            )}
          </FormHelperText>
        </Grid>
      </Grid>

      <ManageEmailVolumeDialog
        open={openManageDialog}
        onClose={() => setOpenManageDialog(false)}
      />
    </CardWrapper>
  );
};

export default AddOnsEmailVolume;
